import type { Cue } from '@core/model/cue.ts';
import type { TrackList } from '@core/model/track.ts';
import { parseCues } from '@core/parsers/index.ts';
import type { SubtitleProvider } from '../../ports/SubtitleProvider.ts';

export type MockTrackDef = {
  value: number;
  name: string;
  code: string;
  /** Path relative to fixtureBaseUrl, e.g. 'youtube.normal.json3.json' */
  fixture: string;
  kind: 'json3-normal' | 'json3-asr';
};

const DEFAULT_TRACKS: MockTrackDef[] = [
  {
    value: 1,
    name: 'English (fixture)',
    code: 'en',
    fixture: 'youtube.normal.json3.json',
    kind: 'json3-normal',
  },
  {
    value: 2,
    name: 'English ASR (fixture)',
    code: 'en',
    fixture: 'youtube.asr.json3.json',
    kind: 'json3-asr',
  },
  {
    value: 3,
    name: 'Chinese (fixture)',
    code: 'zh-TW',
    fixture: 'youtube.normal.json3.json',
    kind: 'json3-normal',
  },
];

export type MockSubtitleProviderOptions = {
  /** Base URL for fixtures (default `/fixtures`). */
  fixtureBaseUrl?: string;
  tracks?: MockTrackDef[];
  fetchImpl?: typeof fetch;
  /** Artificial delay before returning cues (e2e / demos). */
  downloadDelayMs?: number;
};

export class MockSubtitleProvider implements SubtitleProvider {
  readonly id = 'mock';
  readonly label = 'Demo fixtures';
  private readonly fixtureBaseUrl: string;
  private readonly tracks: MockTrackDef[];
  private readonly fetchImpl: typeof fetch;
  private readonly downloadDelayMs: number;

  constructor(options: MockSubtitleProviderOptions = {}) {
    this.fixtureBaseUrl = (options.fixtureBaseUrl ?? '/fixtures').replace(/\/$/, '');
    this.tracks = options.tracks ?? DEFAULT_TRACKS;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    this.downloadDelayMs = Math.max(0, options.downloadDelayMs ?? 0);
  }

  async listTracks(videoId: string): Promise<TrackList> {
    // Deterministic empty seam for E2E / demos (`/?v=empty000000`).
    if (videoId === 'empty000000' || videoId.startsWith('__empty')) {
      return { subtitles: [], translations: [] };
    }
    return {
      subtitles: this.tracks.map(({ name, value, code, kind }) => ({
        name,
        value,
        code,
        downloadable: true,
        kind: kind === 'json3-asr' ? 'asr' : undefined,
      })),
      translations: [],
    };
  }

  async downloadTrack(
    videoId: string,
    trackId: string,
    opts?: { onProgress?: (message: string) => void },
  ): Promise<Cue[]> {
    void videoId;
    opts?.onProgress?.('Downloading…');
    if (this.downloadDelayMs > 0) {
      await new Promise((r) => setTimeout(r, this.downloadDelayMs));
    }
    const id = Number(trackId);
    const def = this.tracks.find((t) => t.value === id);
    if (!def) throw new Error(`Unknown mock track: ${trackId}`);

    const url = `${this.fixtureBaseUrl}/${def.fixture}`;
    const res = await this.fetchImpl(url);
    if (!res.ok) throw new Error(`Failed to load fixture ${def.fixture}: ${res.status}`);
    const json = (await res.json()) as object;
    return parseCues(def.kind, json);
  }
}
